import React, { useEffect, useState } from "react";
import { formatDate, numberFormat } from "../../util/araknerdlib";
import { useBanking } from "../../DataStore/BankingContext";
import { SkeletonBar, SkeletonPill } from "../../util/skeleton/Skeleton";
import { Link } from "react-router-dom";
import { useWallet } from "../../DataStore/WalletContext";

const badgeTone = (s) =>
  s === "Reconciled" ? "success" : s === "Pending" ? "warning" : "secondary";

const ellipsisStyle = (maxWidth) => ({
  maxWidth,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

/* ------------ Table row ------------ */
const TxRow = ({ tx, currency }) => (
  <tr>
    <td style={ellipsisStyle(110)} title={formatDate(tx.cheque_date)}>
      {formatDate(tx.cheque_date)}
    </td>
    <td style={ellipsisStyle(90)} title={tx.payment_type}>
      {tx.payment_type}
    </td>
    <td className="text-muted" style={ellipsisStyle(90)} title={tx.reference}>
      {tx.reference}
    </td>
    <td style={ellipsisStyle(180)} title={tx.cash_account?.account_name}>
      {tx.cash_account?.account_name}
    </td>
    <td className="text-muted" style={ellipsisStyle(260)} title={tx.narration}>
      {tx.narration}
    </td>
    <td
      className="text-right"
      style={ellipsisStyle(140)}
      title={`${currency} ${numberFormat(tx.amount)}`}
    >
      {currency} {numberFormat(tx.amount)}
    </td>
    <td className="text-center" style={ellipsisStyle(110)}>
      <span className={`badge badge-${badgeTone(tx.reconcile_status)}`}>
        {tx.reconcile_status}
      </span>
    </td>
  </tr>
);

/* ------------ Skeleton loader ------------ */
const SkeletonRow = () => (
  <tr>
    <td>
      <SkeletonBar w="70%" h={10} />
    </td>
    <td>
      <SkeletonBar w="60%" h={10} />
    </td>
    <td>
      <SkeletonBar w="40%" h={10} />
    </td>
    <td>
      <SkeletonBar w="85%" h={10} />
    </td>
    <td>
      <SkeletonBar w="90%" h={10} />
    </td>
    <td className="text-right">
      <SkeletonBar
        w="60%"
        h={10}
        style={{ marginLeft: "auto", display: "block" }}
      />
    </td>
    <td className="text-center">
      <SkeletonBar w="70%" h={18} style={{ borderRadius: "999px" }} />
    </td>
  </tr>
);

const SkeletonBody = ({ rows = 3 }) => (
  <>
    {Array.from({ length: rows }).map((_, idx) => (
      <SkeletonRow key={idx} />
    ))}
  </>
);

/* ------------ Aesthetic empty state (matches screenshot vibe) ------------ */
const EmptyRecentTransactions = () => {
  return (
    <div
      className="text-center py-4"
      style={{
        borderRadius: 12,
        border: "1px dashed rgba(0,0,0,0.12)",
        background: "linear-gradient(135deg, rgba(255,255,255,0.85), rgba(245,247,250,0.85))",
      }}
    >
      <div
        className="mx-auto mb-2 d-flex align-items-center justify-content-center"
        style={{
          width: 46,
          height: 46,
          borderRadius: 14,
          background: "rgba(0,123,255,0.10)",
          border: "1px solid rgba(0,123,255,0.18)",
        }}
      >
        <i className="fa fa-receipt text-primary" style={{ fontSize: 18 }} />
      </div>

      <div className="font-weight-semibold" style={{ fontSize: 14 }}>
        No recent transactions yet
      </div>
      <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
        Your latest deposits, withdrawals and transfers will show here.
      </div>

      {/* “visual cue” rows (ghost preview, not loading skeleton) */}
      <div className="mt-3 px-3" style={{ maxWidth: 520, margin: "0 auto" }}>
        <div className="d-flex align-items-center justify-content-between mb-2" style={{ opacity: 0.7 }}>
          <SkeletonBar w="92px" h={10} />
          <SkeletonBar w="70px" h={10} />
          <SkeletonBar w="90px" h={10} />
          <SkeletonPill w={78} h={20} />
        </div>
        <div className="d-flex align-items-center justify-content-between mb-2" style={{ opacity: 0.6 }}>
          <SkeletonBar w="110px" h={10} />
          <SkeletonBar w="70px" h={10} />
          <SkeletonBar w="90px" h={10} />
          <SkeletonPill w={78} h={20} />
        </div>
        <div className="d-flex align-items-center justify-content-between" style={{ opacity: 0.5 }}>
          <SkeletonBar w="96px" h={10} />
          <SkeletonBar w="70px" h={10} />
          <SkeletonBar w="90px" h={10} />
          <SkeletonPill w={78} h={20} />
        </div>
      </div>

      <div className="mt-3">
        <span className="badge badge-light" style={{ borderRadius: 999, padding: "7px 10px" }}>
          Tip: create a payment to populate this list.
        </span>
      </div>
    </div>
  );
};

const RecentTransactions = ({ currency }) => {
  const { getRecentTransactions, recentTransaction } = useWallet();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        if (typeof getRecentTransactions === "function") {
          await getRecentTransactions();
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const hasRows =
    Array.isArray(recentTransaction) && recentTransaction.length > 0;

  const isEmpty404 = recentTransaction === "404";

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        {/* Header always visible */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="h6 mb-0 d-flex align-items-center">
            <i className="fa fa-receipt mr-2 text-primary" />
            Recent Transactions
          </div>

          {!isEmpty404 && (
            <Link
              to="/banking-transactions"
              className="btn btn-sm btn-link p-0 text-primary font-weight-semibold"
            >
              View all <i className="fa fa-arrow-right ml-1" />
            </Link>
          )}
        </div>

        {/* 🔴 HARD EMPTY STATE (404) */}
        {isEmpty404 && !loading && (
          <EmptyRecentTransactions />
        )}

        {/* 🟢 Normal table flow */}
        {!isEmpty404 && (
          <>
            <div
              className="table-responsive overflow-auto"
              style={{ maxHeight: "220px" }}
            >
              <table className="table table-hover table-sm mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>P. Type</th>
                    <th>Reference</th>
                    <th>Bank</th>
                    <th>Description</th>
                    <th className="text-right">Amount</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {loading && <SkeletonBody rows={4} />}

                  {!loading &&
                    hasRows &&
                    recentTransaction.map((t) => (
                      <TxRow key={t.id} tx={t} currency={currency} />
                    ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 d-flex justify-content-end">
              <Link
                to="/banking-transactions"
                className="btn btn-outline-primary btn-sm"
              >
                <i className="fa fa-external-link-alt mr-1" />
                View All
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


export default RecentTransactions;
